import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import { ErrorHandler, ErrorToast } from "utils/toast";
import ButtonLoader from "components/ButtonLoader";
import "../styles/Consent.css";
import { useMutation, useQuery } from "@tanstack/react-query";
import { saveConsentData } from "services/postApis";
import { getUserIpAddress } from "services/getApis";
import { uploadFileToS3 } from "services/aws";

const Consent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const signatureRef = useRef(null);
  const [printedName, setPrintedName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const savedSignatureRef = useRef(null);

  // Save signature after each stroke
  const handleSignatureEnd = useCallback(() => {
    if (signatureRef.current && !signatureRef.current.isEmpty()) {
      savedSignatureRef.current = signatureRef.current.toDataURL();
    }
  }, []);

  // Restore signature if the canvas is resized (mobile scroll / URL bar toggle)
  useEffect(() => {
    const canvas = signatureRef.current?.getCanvas();
    if (!canvas) return;

    const observer = new ResizeObserver(() => {
      if (savedSignatureRef.current && signatureRef.current) {
        signatureRef.current.fromDataURL(savedSignatureRef.current, {
          width: canvas.offsetWidth,
          height: canvas.offsetHeight,
        });
      }
    });

    observer.observe(canvas);
    return () => observer.disconnect();
  }, []);

  const userSubmissionId = location.state?.userSubmissionId;
  const bookAppointmentDescription = location.state?.bookAppointmentDescription;

  const todayDate = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  // Fetch user's IP address using React Query
  const { data: ipAddress = "unknown" } = useQuery({
    queryKey: ["userIpAddress"],
    queryFn: getUserIpAddress,
    staleTime: Infinity,
    cacheTime: Infinity,
    retry: false,
  });

  const clearSignature = () => {
    signatureRef.current?.clear();
    savedSignatureRef.current = null;
  };

  const { mutate: mutateConsent } = useMutation({
    mutationFn: saveConsentData,
    onSuccess: (data) => {
      navigate(`/consultation-fee/${userSubmissionId}`, {
        replace: true,
        state: {
          userSubmissionId: userSubmissionId,
          bookAppointmentDescription: bookAppointmentDescription,
        },
      });
    },
    onError: (error) => {
      console.log("Order Error", error);
      ErrorHandler("Failed to place order. Please try again.");
    },
    onSettled: () => {
      setIsLoading(false);
    },
  });

  const handleSubmit = async () => {
    if (!printedName.trim()) {
      ErrorToast("Please enter your name.");
      return;
    }

    if (signatureRef.current?.isEmpty()) {
      ErrorToast("Please provide your signature.");
      return;
    }

    try {
      setIsLoading(true);
      const signatureData = signatureRef.current.toDataURL();

      // Convert base64 signature to File object
      const base64Response = await fetch(signatureData);
      const blob = await base64Response.blob();
      const signatureFile = new File(
        [blob],
        `signature_${printedName.replace(/\s+/g, "_")}_${Date.now()}.png`,
        { type: "image/png" },
      );

      // Upload signature to S3
      const uploadResult = await uploadFileToS3(
        signatureFile,
        "public/signature",
      );

      // Extract just the filename from the key (e.g., "public/signature/name_123_abc.png" -> "name_123_abc.png")
      const fileName = uploadResult.key.split("/").pop();

      const consentData = {
        userSubmissionId: userSubmissionId,
        name: printedName,
        ipAddress: ipAddress || "unknown",
        signature: fileName,
        acceptedAt: new Date().toISOString(),
      };
      mutateConsent(consentData);
    } catch (error) {
      console.error("Consent submission error:", error);
      ErrorToast("Failed to submit consent. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!userSubmissionId) {
    navigate("/questionnaire", { replace: true });
    return null;
  }

  return (
    <div className="questionnaire-container">
      <div className="questionnaire-content" style={{ maxWidth: "900px" }}>
        <div className="content-card">
          <h2 className="question-title" style={{ textAlign: "center" }}>
            Consent & Agreement
          </h2>

          <div className="consent-container">
            {/* TCM Consent */}
            <div className="consent-section">
              <h3 className="consent-heading">
                TRADITIONAL CHINESE MEDICINE CONSENT
              </h3>
              <p className="consent-text">
                I understand that services provided by Hua Shen Herbs are based
                on traditional Chinese medicine (TCM), which is a holistic
                system of wellness. These services are not a substitute for
                conventional medical diagnosis or treatment.
              </p>
              <p className="consent-text">I acknowledge that:</p>
              <ul className="consent-list">
                <li>No guarantees have been made regarding outcomes</li>
                <li>
                  Recommendations are based on traditional theory and
                  individualized assessment
                </li>
                <li>
                  I am responsible for consulting a licensed physician regarding
                  any medical condition
                </li>
              </ul>
            </div>

            {/* Herbal Medicine Consent */}
            <div className="consent-section">
              <h3 className="consent-heading">HERBAL MEDICINE CONSENT</h3>
              <p className="consent-text">
                I understand that I may receive customized herbal formulations.
              </p>
              <p className="consent-text">I acknowledge that:</p>
              <ul className="consent-list">
                <li>
                  Herbal products are not evaluated by the Food and Drug
                  Administration
                </li>
                <li>Results may vary based on individual response</li>
                <li>
                  It is my responsibility to follow dosage instructions
                  carefully
                </li>
              </ul>
              <p className="consent-text">I agree to:</p>
              <ul className="consent-list">
                <li>Disclose all medications, supplements, and allergies</li>
                <li>
                  Discontinue use and seek medical care if any adverse reaction
                  occurs
                </li>
              </ul>
            </div>

            {/* Telehealth Consent */}
            <div className="consent-section">
              <h3 className="consent-heading">TELEHEALTH CONSENT</h3>
              <p className="consent-text">
                I consent to receiving services via telehealth when applicable.
              </p>
              <p className="consent-text">I understand that:</p>
              <ul className="consent-list">
                <li>
                  Telehealth involves electronic communication and may carry
                  risks such as technical issues or privacy limitations
                </li>
                <li>I may withdraw consent at any time</li>
                <li>Telehealth may not be appropriate for all conditions</li>
              </ul>
            </div>

            {/* Financial Policy */}
            <div className="consent-section">
              <h3 className="consent-heading">FINANCIAL POLICY</h3>
              <p className="consent-text">I understand that:</p>
              <ul className="consent-list">
                <li>Payment is required at the time of service or purchase</li>
                <li>Customized herbal formulations are non-refundable</li>
                <li>
                  Insurance reimbursement, FSA, or HSA eligibility is not
                  guaranteed
                </li>
              </ul>
            </div>

            {/* Release of Liability */}
            <div className="consent-section">
              <h3 className="consent-heading">RELEASE OF LIABILITY</h3>
              <p className="consent-text">
                I voluntarily assume full responsibility for my participation in
                services and use of herbal products provided by Hua Shen Herbs.
              </p>
              <p className="consent-text">
                I release Hua Shen Herbs and its practitioners from liability
                for any adverse effects or outcomes, except where prohibited by
                law.
              </p>
            </div>

            {/* Signature Section */}
            <div className="consent-section signature-section">
              <h3 className="consent-heading">ACKNOWLEDGMENT AND SIGNATURE</h3>
              <p className="consent-text" style={{ marginBottom: "24px" }}>
                By signing below, I confirm that I have read, understood, and
                agree to the above information and consent to receive services
                from Hua Shen Herbs.
              </p>

              {/* Printed Name */}
              <div className="form-group">
                <label htmlFor="printedName" className="form-label">
                  Printed Name *
                </label>
                <input
                  type="text"
                  id="printedName"
                  className="form-input"
                  placeholder="Enter your full name"
                  value={printedName}
                  onChange={(e) => setPrintedName(e.target.value)}
                  maxLength={100}
                />
              </div>

              {/* Signature Canvas */}
              <div className="form-group">
                <label className="form-label">Digital Signature *</label>
                <div className="signature-wrapper">
                  <SignatureCanvas
                    ref={signatureRef}
                    canvasProps={{
                      className: "signature-canvas",
                    }}
                    backgroundColor="#ffffff"
                    onEnd={handleSignatureEnd}
                  />
                </div>
                <button
                  type="button"
                  className="clear-signature-btn"
                  onClick={clearSignature}
                >
                  Clear Signature
                </button>
              </div>

              {/* Date */}
              <div className="form-group">
                <label className="form-label">Date</label>
                <input
                  type="text"
                  className="form-input"
                  value={todayDate}
                  readOnly
                  disabled
                />
              </div>
            </div>
          </div>

          <button
            className={`continue-btn ${
              printedName.trim() ? "active" : "disabled"
            }`}
            onClick={handleSubmit}
            disabled={!printedName.trim() || isLoading}
          >
            {isLoading ? <ButtonLoader /> : "Continue to Payment"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Consent;
